import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Loader2, Send } from "lucide-react";
import { cn, formatINR } from "@/lib/utils";

interface SendQuoteDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  conversationId: string;
  dealId: string;
  windowOpen: boolean;
  onSent: () => void;
}

/** Sends an approved quotation's PDF into the WhatsApp chat */
export function SendQuoteDialog({ open, onOpenChange, conversationId, dealId, windowOpen, onSent }: SendQuoteDialogProps) {
  const { success, error } = useToast();
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [quoteId, setQuoteId] = useState("");
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setQuoteId("");
    setCaption("");
    api
      .get(`/api/Quotation/List?deal_id=${dealId}&page_size=100`)
      .then((res) => setQuotes(unwrapPage<any>(res).rows.filter((q) => ["APPROVED", "SENT", "ACCEPTED"].includes(q.quote_status))))
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  }, [open, dealId]);

  const send = async () => {
    setSending(true);
    try {
      await api.post(`/api/WhatsApp/Conversations/${conversationId}/SendQuote`, { quote_id: quoteId, caption: caption.trim() || undefined });
      success("Quotation sent on WhatsApp");
      onOpenChange(false);
      onSent();
    } catch (err: any) {
      error("Not sent", err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send quotation on WhatsApp</DialogTitle>
          <DialogDescription>Only approved quotations can be sent.</DialogDescription>
        </DialogHeader>
        {!windowOpen ? (
          <p className="text-sm text-warning">
            The 24-hour reply window is closed. Use "Send template" with a template that has a document header, and pick the
            quotation there.
          </p>
        ) : loading ? (
          <div className="py-6 flex justify-center">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : quotes.length === 0 ? (
          <p className="text-sm text-muted-foreground">This deal has no approved quotation yet.</p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              {quotes.map((q) => (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => setQuoteId(q.id)}
                  className={cn(
                    "w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left hover:bg-muted/50",
                    quoteId === q.id && "border-primary bg-primary/5"
                  )}
                >
                  <FileText className="size-4 text-primary" />
                  <span className="font-mono text-xs font-semibold flex-1">{q.display_number}</span>
                  <span className="text-sm font-semibold">{formatINR(q.grand_total)}</span>
                </button>
              ))}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Message with the PDF (optional)</Label>
              <Textarea rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Please find our quotation attached." />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {windowOpen && (
            <Button onClick={send} disabled={!quoteId || sending} className="gap-1.5">
              {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              Send PDF
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
